import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Pacientes.css';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';
import { useNotification } from '../components/NotificationContext';

const Pacientes = () => {
  const { showNotification } = useNotification();
  const [allPatients, setAllPatients] = useState([]);
  const [limit, setLimit] = useState(5);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // Modal de cadastro de novo paciente
  const [showModal, setShowModal] = useState(false);

  // Formulário de cadastro
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    genero: '',
    idade: '',
    cep: '',
    cidade: '',
    endereco: '',
    email: '',
    detalhesMedicos: '',
    procedimentos: '',
    proximaConsulta: ''
  });

  // ---------- ESTADOS PARA O MODAL DE DETALHES ----------
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [showAddConsultationForm, setShowAddConsultationForm] = useState(false);
  const [negocios, setNegocios] = useState([]);

  // Função para buscar os dados dos pacientes
  const fetchPatientData = async () => {
    setLoading(true);
  
    try {
      const userId = localStorage.getItem("patientId");
      const authToken = localStorage.getItem("authToken");
  
      if (!userId) {
        console.error("User ID não encontrado no localStorage.");
        setLoading(false);
        return;
      }
  
      // ✅ Verifica se os dados já estão em cache
      const cachedData = localStorage.getItem("pacientes_cache");
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        setAllPatients(parsedData);
        setLoading(false);
        return;
      }
  
      // 🔄 Se não houver cache, faz a requisição
      const response = await fetch("https://x8ki-letl-twmt.n7.xano.io/api:g_BmLeE8/pacientes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ user_id: userId }),
      });
  
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
  
      const data = await response.json();
      console.log("Dados dos pacientes:", data);
  
      // ✅ Salva no estado e no cache
      setAllPatients(data);
      localStorage.setItem("pacientes_cache", JSON.stringify(data));
    } catch (error) {
      console.error("Erro ao buscar dados dos pacientes:", error);
    } finally {
      setLoading(false);
    }
  };
  

  // Dispara a busca assim que o componente é montado
  useEffect(() => {
    fetchPatientData();
  }, []);

  // Filtra os pacientes com base no termo pesquisado
  const filteredPatients = allPatients.filter((patient) =>
    patient.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Define os pacientes que serão exibidos conforme o limite escolhido
  const displayedPatients = filteredPatients.slice(0, limit);

  // Abre o modal de cadastro
  const handleAddPatient = () => {
    setShowModal(true);
  };

  // Fecha o modal de cadastro
  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Atualiza os campos do formulário de cadastro
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Busca endereço via CEP
  const handleBuscarEndereco = async () => {
    const cep = formData.cep.trim();
    if (cep.length === 8 && /^[0-9]+$/.test(cep)) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        if (data.erro) {
          showNotification('CEP inválido. Verifique e tente novamente.', 'error');
          return;
        }
        setFormData((prev) => ({
          ...prev,
          cidade: data.localidade || '',
          endereco: `${data.logradouro || ''}${data.bairro ? ', ' + data.bairro : ''}`
        }));
      } catch (error) {
        console.error('Erro ao buscar o CEP:', error);
        showNotification('Erro ao buscar o CEP. Tente novamente mais tarde.', 'error');
      }
    } else if (cep) {
      showNotification('Formato de CEP inválido. Use apenas números com 8 dígitos.', 'warning');
    }
  };

  // Envio do formulário de cadastro
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome.trim() || !formData.cpf.trim() || !formData.email.trim()) {
      showNotification('Os campos Nome, CPF e Email são obrigatórios.', 'warning');
      return;
    }

    const userId = localStorage.getItem('patientId');
    const parsedUserId = parseInt(userId, 10);
    if (isNaN(parsedUserId)) {
      showNotification("Ocorreu um erro com o ID do usuário. Faça login novamente.", "error");
      return;
    }

    const patientData = {
      Name: formData.nome,
      CPF: formData.cpf,
      email: formData.email,
      genero: formData.genero,
      idade: formData.idade,
      cep: formData.cep,
      cidade: formData.cidade,
      endereco: formData.endereco,
      detalhesMedicos: formData.detalhesMedicos,
      procedimentos: formData.procedimentos,
      proximaConsulta: formData.proximaConsulta,
      user_id: parsedUserId
    };

    console.log('Dados do paciente:', patientData);

    try {
      const response = await fetch('https://x8ki-letl-twmt.n7.xano.io/api:g_BmLeE8/paciente', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(patientData)
      });

      if (!response.ok) {
        throw new Error('Erro na requisição: ' + response.statusText);
      }

      await response.json();
      showNotification('Paciente adicionado com sucesso!', 'success');
      handleCloseModal();
      fetchPatientData();

      // Reseta os campos do formulário
      setFormData({
        nome: '',
        cpf: '',
        genero: '',
        idade: '',
        cep: '',
        cidade: '',
        endereco: '',
        email: '',
        detalhesMedicos: '',
        procedimentos: '',
        proximaConsulta: ''
      });
    } catch (error) {
      console.error('Erro ao registrar o paciente:', error);
      showNotification("Ocorreu um erro ao tentar registrar o paciente. Tente novamente.", "error");
    }
  };

  // ---------- FUNÇÕES DO MODAL DE DETALHES ----------

  // Abre o modal com detalhes do paciente + consultas
  const viewPatientDetails = async (patientId) => {
    const patient = allPatients.find((p) => p.id === patientId);
    if (!patient) {
      console.error('Dados do paciente não encontrados.');
      return;
    }

    try {
      setLoading(true);

      // Buscar consultas do paciente
      const response = await fetch(
        'https://x8ki-letl-twmt.n7.xano.io/api:bf7mCpGF/busca_consultas',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ paciente_id: patientId }),
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao buscar consultas do paciente.');
      }

      const data = await response.json();

      setSelectedPatient(patient);
      setConsultations(data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para excluir consulta
  const excluirConsulta = async (consultaId, consultaTitulo) => {
    if (window.confirm(`Você tem certeza que deseja excluir a consulta '${consultaTitulo}'?`)) {
      try {
        const url = `https://x8ki-letl-twmt.n7.xano.io/api:bf7mCpGF/consultas/${consultaId}`;
        const response = await fetch(url, {
          method: 'DELETE'
        });
        if (!response.ok) {
          throw new Error('Falha ao excluir a consulta');
        }
        await response.json();
        showNotification('Consulta excluída com sucesso!', 'success');
        setShowDetailsModal(false);
        fetchPatientData();
      } catch (error) {
        console.error('Erro ao excluir consulta:', error);
        showNotification('Não foi possível excluir a consulta. Por favor, tente novamente.', 'error');
      }
    }
  };

  // Função para excluir paciente
  const deletePatient = async (patientId) => {
    if (window.confirm('Você tem certeza que deseja excluir este paciente?')) {
      try {
        const url = `https://x8ki-letl-twmt.n7.xano.io/api:g_BmLeE8/paciente/${patientId}`;
        const response = await fetch(url, {
          method: 'DELETE',
        });
        if (!response.ok) {
          throw new Error('Falha ao excluir o paciente');
        }
        await response.json();
        showNotification('Paciente excluído com sucesso!', 'success');
        setShowDetailsModal(false);
        fetchPatientData();
      } catch (error) {
        showNotification('Não foi possível excluir o paciente. Por favor, tente novamente.', 'error');
      }
    }
  };

  // Exibe ou oculta o formulário de "Nova Consulta"
  const openAddConsultationDiv = () => {
    setShowAddConsultationForm((prev) => !prev);
  };

  // Função para adicionar nova consulta (placeholder)
  const addNewConsultation = (patientId) => {
    showNotification(`Adicionando nova consulta para o paciente ID: ${patientId}`, 'info');
    // Lógica real de criação de consulta aqui...
  };

  // Carrega os negócios para o select do formulário de "Nova Consulta"
  const carregarNegociosParaSelect = async () => {
    if (negocios.length > 0) return; // Evita re-fetch se já carregado
    try {
      const response = await fetch('https://x8ki-letl-twmt.n7.xano.io/api:1gyP90jK/negocios');
      const data = await response.json();
      setNegocios(data);
    } catch (error) {
      console.error('Erro ao carregar negócios:', error);
    }
  };

  return (
    <div className="pacientes-page">
      <div className="pacientes-header">
        <div className="pacientes-header-left">
          <h2>Pacientes</h2>
          <div className="pacientes-controls">
            <label htmlFor="limit">Número de pacientes por página:</label>
            <select
              id="limit"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        <div className="pacientes-header-right">
          <input
            type="text"
            placeholder="Pesquisar paciente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pacientes-search-bar"
          />
          <button
            onClick={handleAddPatient}
            className="pacientes-add-button"
            title="Adicionar Paciente"
          >
            <FaPlus />
          </button>
        </div>
      </div>

      {loading && <p className="pacientes-loading">Carregando...</p>}
      {!loading && filteredPatients.length === 0 && (
        <p className="pacientes-no-patients">Nenhum paciente encontrado.</p>
      )}

      <div className="pacientes-container">
        {displayedPatients.map((patient) => (
          <div key={patient.id} className="pacientes-section">
            <div className="pacientes-left-content">
              <h3>{patient.Name}</h3>
              <p>{patient.email}</p>
            </div>
            <div className="pacientes-right-content">
              <p>
                <strong>
                  Cashback: R${' '}
                  {patient.cashback
                    ? patient.cashback.toFixed(2).replace('.', ',')
                    : '0,00'}
                </strong>
              </p>
              <button onClick={() => viewPatientDetails(patient.id)}>
                Ver detalhes
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* MODAL DE CADASTRO DE NOVO PACIENTE */}
      {showModal && (
        <div className="pacientes-modal">
          <div className="pacientes-modal-content">
            <span className="pacientes-modal-close" onClick={handleCloseModal}>
              &times;
            </span>
            <h2>Cadastro de Paciente</h2>
            <form onSubmit={handleSubmit} className="pacientes-form">
              <div className="pacientes-form-group">
                <label>Nome:</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="pacientes-form-group">
                <label>CPF:</label>
                <input
                  type="text"
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="pacientes-form-group">
                <label>Gênero:</label>
                <select
                  name="genero"
                  value={formData.genero}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Selecione</option>
                  <option value="Masculino">Masculino</option>
                  <option value="Feminino">Feminino</option>
                  <option value="Outro">Outro</option>
                </select>
              </div>
              <div className="pacientes-form-group">
                <label>Idade:</label>
                <input
                  type="number"
                  name="idade"
                  value={formData.idade}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="pacientes-form-group">
                <label>CEP:</label>
                <input
                  type="text"
                  name="cep"
                  value={formData.cep}
                  onChange={handleInputChange}
                  onBlur={handleBuscarEndereco}
                  required
                />
              </div>
              <div className="pacientes-form-group">
                <label>Cidade:</label>
                <input
                  type="text"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleInputChange}
                />
              </div>
              <div className="pacientes-form-group">
                <label>Endereço:</label>
                <input
                  type="text"
                  name="endereco"
                  value={formData.endereco}
                  onChange={handleInputChange}
                />
              </div>
              <div className="pacientes-form-group">
                <label>Email:</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="pacientes-form-group">
                <label>Detalhes Médicos:</label>
                <textarea
                  name="detalhesMedicos"
                  value={formData.detalhesMedicos}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>
              <div className="pacientes-form-group">
                <label>Procedimentos:</label>
                <textarea
                  name="procedimentos"
                  value={formData.procedimentos}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>
              <div className="pacientes-form-group">
                <label>Data da Próxima Consulta:</label>
                <input
                  type="text"
                  name="proximaConsulta"
                  placeholder="dd/mm/aaaa"
                  value={formData.proximaConsulta}
                  onChange={handleInputChange}
                />
              </div>
              <button type="submit" className="pacientes-submit-button">
                Cadastrar
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE DETALHES DO PACIENTE */}
      {showDetailsModal && selectedPatient && (
        <div className="pacientes-details-modal">
          <div className="pacientes-details-modal-content">
            <span
              className="pacientes-details-modal-close"
              onClick={() => setShowDetailsModal(false)}
            >
              &times;
            </span>
            <h2 className="pacientes-details-title">
              {selectedPatient.Name}{' '}
              <FaTrashAlt
                onClick={() => deletePatient(selectedPatient.id)}
                style={{ cursor: 'pointer', marginLeft: '10px', width: '20px', height: '20px' }}
              />
            </h2>
            <p><strong>ID:</strong> {selectedPatient.id}</p>
            <p><strong>Email:</strong> {selectedPatient.email}</p>
            <p>
              <strong>Cashback:</strong>{' '}
              R${selectedPatient.cashback?.toFixed(2).replace('.', ',') || '0,00'}
            </p>
            <p>
              <strong>Criado em:</strong>{' '}
              {new Date(selectedPatient.created_at).toLocaleDateString()}
            </p>

            <h3>Consultas</h3>
            {consultations.length > 0 ? (
              <ul className="pacientes-consultations-list">
                {consultations.map((consulta) => {
                  const formattedDate = new Date(consulta.data_consulta).toLocaleDateString();
                  return (
                    <li key={consulta.id} className="pacientes-consultation-item">
                      <div className="pacientes-consultation-header">
                        <span className="pacientes-consultation-title">
                          <strong>{consulta.titulo}</strong>
                        </span>
                        <span className="pacientes-consultation-value">
                          R$ {consulta.valor_pago.toFixed(2).replace('.', ',')}
                        </span>
                        <span
                          className="pacientes-edit-icon"
                          onClick={() => excluirConsulta(consulta.id, consulta.titulo)}
                        >
                          <FaTrashAlt style={{ width: '20px', height: '20px' }}  />
                        </span>
                      </div>
                      <div className="pacientes-consultation-details">
                        <span className="pacientes-consultation-date">
                          Data: {formattedDate}
                        </span>
                        <span className="pacientes-consultation-cashback">
                          Cashback: {consulta.cashback_percentual || 0}%
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p>Nenhuma consulta encontrada.</p>
            )}

            {/* Botão de "Nova Consulta" */}
            <button
              onClick={openAddConsultationDiv}
              style={{ width: '100%', marginTop: '10px' }}
            >
              Nova Consulta
            </button>

            {/* Formulário de "Nova Consulta" (exemplo) */}
            {showAddConsultationForm && (
              <div className="pacientes-add-consultation-div">
                <div className="pacientes-add-consultation-fields">
                  <input type="text" placeholder="Título" />
                  <input type="number" placeholder="Valor Pago" />
                  <input type="date" placeholder="Data da Consulta" />
                </div>
                <div className="pacientes-add-consultation-negocios">
                  <label>Selecionar Negócio:</label>
                  <select onFocus={carregarNegociosParaSelect}>
                    {negocios.length === 0 ? (
                      <option value="">Carregando negócios...</option>
                    ) : (
                      negocios.map((negocio) => (
                        <option key={negocio.id} value={negocio.id}>
                          {negocio.nome}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div className="pacientes-add-consultation-payment">
                  <form>
                    <div>
                      <label htmlFor="pix">Pix</label>
                      <input type="radio" id="pix" name="paymentMethod" value="pix" />
                    </div>
                    <div>
                      <label htmlFor="credit_card">Cartão de Crédito</label>
                      <input type="radio" id="credit_card" name="paymentMethod" value="credit_card" />
                    </div>
                    <div>
                      <label htmlFor="debit_card">Cartão de Débito</label>
                      <input type="radio" id="debit_card" name="paymentMethod" value="debit_card" />
                    </div>
                  </form>

                  <div className="pacientes-funds-switch">
                    <label>Utilizar fundos que o cliente já possui</label>
                    <input type="checkbox" />
                  </div>
                </div>
                <button
                  onClick={() => addNewConsultation(selectedPatient.id)}
                  style={{ width: '100%', marginTop: '10px' }}
                >
                  Adicionar Consulta
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Pacientes;
